import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Edit2, X, ShoppingCart, Building2,
  Package, MapPin, FileText, User, Mail, Phone, Trash2, CheckCircle, Truck, Calendar, Gift, Clock, Download
} from 'lucide-react';
import api, { API_BASE_URL } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useAuth } from '../../contexts/AuthContext';
import { useAlert } from '@/components/AlertProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

interface PurchaseOrder {
  id: number;
  order_number: string;
  client_id: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'invoiced' | 'paid' | 'cancelled' | 'enregistrer';
  total_amount: number;
  subtotal: number;
  tax_amount: number;
  currency: string;
  tier_breakdown?: string | null;
  delivery_address?: string;
  delivery_city?: string;
  delivery_postal_code?: string;
  delivery_country?: string;
  notes?: string;
  admin_notes?: string;
  created_at: string;
  updated_at: string;
  confirmed_at?: string;
  shipped_at?: string;
  delivered_at?: string;
  received_at?: string;
  received_by_client_member_id?: number;
  paid_at?: string;
  paid_by_admin_id?: number;
  enregistrer_at?: string;
  enregistrer_by_admin_id?: number;
  pdf_url?: string;
  quote_id?: number;
  // Tracking fields
  tracking_number?: string;
  current_location?: string;
  estimated_delivery_date?: string;
  tracking_notes?: string;
  last_tracking_update?: string;
  client?: {
    id: number;
    company_name: string;
    first_name?: string;
    last_name?: string;
    email: string;
    phone?: string;
    admin_id?: number;
    admin?: {
      id: number;
      email: string;
      first_name?: string;
      last_name?: string;
    };
  };
  items?: Array<{
    id: number;
    consumable_id: number;
    quantity: number;
    unit_price: number;
    discount_percentage: number;
    line_total: number;
    product_title: string;
    product_unit: string;
    batch_size: number;
    consumable?: {
      title: string;
      unit: string;
    };
  }>;
}

interface TierInfo {
  tier_number: number;
  min_quantity: number;
  discount_percentage?: number | null;
  free_units?: number | null;
  client_price: number;
  triggered: boolean;
}

interface UnitTypeTierData {
  unit_name: string;
  marque?: string;
  gamme?: string;
  batch_size: number;
  total_lots_ordered: number;
  catalog_price: number;
  tiers: TierInfo[];
  tier_mode: 'ug' | 'percent';
}

interface TierBreakdown {
  [key: string]: UnitTypeTierData;
}

const statusOptions = [
  { value: 'pending', label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'confirmed', label: 'Confirmé', color: 'bg-blue-100 text-blue-700' },
  { value: 'shipped', label: 'Expédié', color: 'bg-purple-100 text-purple-700' },
  { value: 'delivered', label: 'Livré', color: 'bg-green-100 text-green-700' },
  { value: 'enregistrer', label: 'Enregistré', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'invoiced', label: 'Facturé', color: 'bg-amber-100 text-amber-700' },
  { value: 'paid', label: 'Payé', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'cancelled', label: 'Annulé', color: 'bg-gray-100 text-gray-700' },
];

const formatDate = (value?: string): string => {
  if (!value) return '';

  // Handle YYYY-MM-DD without timezone shifting
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('fr-FR');
  }

  return new Date(value).toLocaleDateString('fr-FR');
};

const normalizeUnitName = (value: string): string => {
  return value
    .toLowerCase()
    .replace(/\s*\(.*?\)/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/s$/, '');
};

const getUnitLabel = (value?: string): string => {
  if (!value) return 'unité';
  return value.replace(/\s*\(Lot de\s*\d+\)/i, '').trim();
};

const getStatusDateInfo = (order: PurchaseOrder): { label: string; value: string } | null => {
  let label: string;
  let dateValue: string | undefined;

  switch (order.status) {
    case 'paid':
      label = 'Date de paiement';
      dateValue = order.paid_at || order.enregistrer_at || order.updated_at;
      break;
    case 'invoiced':
      label = 'Date de facturation';
      dateValue = order.updated_at;
      break;
    case 'confirmed':
      label = 'Date de confirmation';
      dateValue = order.confirmed_at || order.updated_at;
      break;
    case 'shipped':
      label = "Date d'expédition";
      dateValue = order.shipped_at || order.updated_at;
      break;
    case 'delivered':
      label = 'Date de livraison';
      dateValue = order.delivered_at || order.updated_at;
      break;
    case 'enregistrer':
      label = "Date d'enregistrement";
      dateValue = order.enregistrer_at || order.updated_at;
      break;
    case 'cancelled':
      label = "Date d'annulation";
      dateValue = order.updated_at;
      break;
    case 'pending':
    default:
      label = 'Dernière mise à jour';
      dateValue = order.updated_at;
      break;
  }

  if (!dateValue) return null;
  const formatted = formatDate(dateValue);
  if (!formatted) return null;
  return { label, value: formatted };
};

interface WorkflowStep {
  label: string;
  date?: string;
  status: 'completed' | 'current' | 'pending' | 'cancelled';
  icon: React.ElementType;
}

const getWorkflowSteps = (order: PurchaseOrder): WorkflowStep[] => {
  const isCancelled = order.status === 'cancelled';

  const steps: WorkflowStep[] = [
    {
      label: 'Créée',
      date: order.created_at,
      status: 'completed',
      icon: ShoppingCart
    },
    {
      label: 'Confirmée',
      date: order.confirmed_at,
      status: isCancelled ? 'cancelled' : (order.confirmed_at ? 'completed' : (['pending'].includes(order.status) ? 'pending' : 'completed')),
      icon: CheckCircle
    },
    {
      label: 'Expédiée',
      date: order.shipped_at,
      status: isCancelled ? 'cancelled' : (order.shipped_at ? 'completed' : (['shipped', 'delivered', 'invoiced', 'enregistrer', 'paid'].includes(order.status) ? 'current' : 'pending')),
      icon: Truck
    },
    {
      label: 'Livrée',
      date: order.delivered_at,
      status: isCancelled ? 'cancelled' : (order.delivered_at ? 'completed' : (['delivered', 'invoiced', 'enregistrer', 'paid'].includes(order.status) ? 'current' : 'pending')),
      icon: Package
    },
    {
      label: 'Facturée',
      date: order.status === 'invoiced' || order.status === 'paid' || order.status === 'enregistrer' ? order.updated_at : undefined,
      status: isCancelled ? 'cancelled' : (['invoiced', 'enregistrer', 'paid'].includes(order.status) ? 'completed' : 'pending'),
      icon: FileText
    },
    {
      label: 'Payée',
      date: order.paid_at || order.enregistrer_at,
      status: isCancelled ? 'cancelled' : (order.status === 'paid' ? 'completed' : 'pending'),
      icon: CheckCircle
    }
  ];

  return steps;
};

const PurchaseOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError, showInfo } = useAlert();

  const isApporteur = user?.role === 'apporteur';

  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedOrder, setEditedOrder] = useState<Partial<PurchaseOrder>>({});
  const [existingInvoice, setExistingInvoice] = useState<any | null>(null);

  const [pdfType, setPdfType] = useState<'bdc' | 'bdl' | 'invoice' | null>(null);
  const [loadingPdf, setLoadingPdf] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const [showEnregistrerPopup, setShowEnregistrerPopup] = useState<boolean>(false);
  const [enregistrerDate, setEnregistrerDate] = useState<string>('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; type: 'order' | 'item'; itemId?: number }>({ open: false, type: 'order' });
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const tierBreakdownParsed = useMemo<TierBreakdown | null>(() => {
    if (!order?.tier_breakdown) return null;
    try {
      return JSON.parse(order.tier_breakdown) as TierBreakdown;
    } catch {
      return null;
    }
  }, [order?.tier_breakdown]);

  const fetchExistingInvoice = useCallback(async (orderId?: number) => {
    if (!orderId) {
      setExistingInvoice(null);
      return;
    }

    try {
      const invoiceResponse = await api.get(`/invoices/by-purchase-order/${orderId}` as const, {
        validateStatus: (status) => status === 200 || status === 204,
      });
      if (invoiceResponse.status === 204) {
        setExistingInvoice(null);
      } else {
        const inv = invoiceResponse.data as any;
        setExistingInvoice(inv ?? null);
      }
    } catch (error: any) {
      console.error('[PO DETAIL] Failed to fetch invoice for order', error);
      setExistingInvoice(null);
    }
  }, []);

  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/purchase-orders/${id}`);
      setOrder(response.data);
      setEditedOrder(response.data);
      await fetchExistingInvoice(response.data?.id);
    } catch (error: any) {
      showError(error.response?.data?.detail || "Impossible de charger le bon de commande");
      navigate('/achat/orders');
    } finally {
      setLoading(false);
    }
  }, [id, showError, navigate, fetchExistingInvoice]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleSave = async () => {
    try {
      setSaving(true);

      // Update basic order information
      await api.patch(`/purchase-orders/${id}`, {
        status: editedOrder.status,
        notes: editedOrder.notes,
        delivery_address: editedOrder.delivery_address,
        delivery_city: editedOrder.delivery_city,
        delivery_postal_code: editedOrder.delivery_postal_code,
        delivery_country: editedOrder.delivery_country,
      });

      // Update tracking information
      await api.put(`/purchase-orders/${id}/tracking`, {
        tracking_number: editedOrder.tracking_number,
        current_location: editedOrder.current_location,
        estimated_delivery_date: editedOrder.estimated_delivery_date,
        tracking_notes: editedOrder.tracking_notes,
      });

      showSuccess("Bon de commande et informations de suivi mises à jour avec succès");

      setEditMode(false);
      await loadOrder();
    } catch (error: any) {
      showError(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    setDeleteDialog({ open: true, type: 'order' });
  };

  const handleDeleteItem = (itemId: number) => {
    setDeleteDialog({ open: true, type: 'item', itemId });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.open) return;

    setIsDeleting(true);
    try {
      if (deleteDialog.type === 'order') {
        await api.delete(`/purchase-orders/${id}`);
        showSuccess("Bon de commande supprimé");
        setDeleteDialog({ open: false, type: 'order' });
        navigate('/achat/orders');
      } else if (deleteDialog.type === 'item' && deleteDialog.itemId) {
        await api.delete(`/purchase-orders/${id}/items/${deleteDialog.itemId}`);
        showSuccess("Ligne supprimée avec succès");
        setDeleteDialog({ open: false, type: 'order' });
        await loadOrder();
      }
    } catch (error: any) {
      showError(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const closePdfViewer = (): void => {
    setPdfUrl(null);
    setPdfType(null);
  };

  const handleViewPdf = async (type: 'bdc' | 'bdl' | 'invoice'): Promise<void> => {
    if (!order) return;

    if (pdfType === type) {
      closePdfViewer();
      return;
    }

    closePdfViewer();
    setPdfType(type);
    setLoadingPdf(true);
    setPdfUrl(null);

    const token = useAuthStore.getState().token;
    if (!token) { setLoadingPdf(false); return; }

    try {
      if (type === 'bdc') {
        setPdfUrl(`${API_BASE_URL}/purchase-orders/${order.id}/pdf?token=${encodeURIComponent(token)}`);
      } else if (type === 'bdl') {
        setPdfUrl(`${API_BASE_URL}/purchase-orders/${order.id}/delivery-note/pdf?token=${encodeURIComponent(token)}`);
      } else {
        let currentInvoice = existingInvoice;

        if (!currentInvoice) {
          if (!['delivered', 'invoiced', 'paid', 'enregistrer'].includes(order?.status || '')) {
            throw new Error('Invoice not allowed for this status');
          }
          showInfo('Génération de la facture...');
          const createRes = await api.post('/invoices/from-purchase-order', {
            purchase_order_id: order?.id,
            percentage: 100,
            due_days: 30,
          });
          currentInvoice = createRes.data;
          setExistingInvoice(currentInvoice);
          showSuccess('Facture créée avec succès');
        }

        if (!currentInvoice) throw new Error('Invoice missing');
        setPdfUrl(`${API_BASE_URL}/invoices/${currentInvoice.id}/pdf?token=${encodeURIComponent(token)}`);
      }
    } catch (error: any) {
      console.error('Erreur chargement PDF:', error);
      if (type === 'bdc') showError(error.response?.data?.detail || "Impossible d'afficher le bon de commande");
      else if (type === 'bdl') showError(error.response?.data?.detail || "Impossible d'afficher le bon de livraison");
      else showError(error.response?.data?.detail || "Impossible d'afficher la facture");
      setPdfType(null);
      setPdfUrl(null);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleViewBDC = (): void => {
    void handleViewPdf('bdc');
  };

  const handleViewDeliveryNote = (): void => {
    void handleViewPdf('bdl');
  };

  const handleViewInvoice = (): void => {
    void handleViewPdf('invoice');
  };

  const handleConfirmOrder = async () => {
    if (!order) return;

    try {
      await api.patch(`/purchase-orders/${id}`, {
        status: 'confirmed'
      });

      showSuccess(`Le bon de commande ${order.order_number} est maintenant confirmé.`);

      await loadOrder();
    } catch (error: any) {
      showError(error.response?.data?.detail || "Impossible de confirmer la commande");
    }
  };

  const handleMarkShipped = async () => {
    if (!order) return;

    try {
      await api.patch(`/purchase-orders/${id}`, {
        status: 'shipped'
      });

      showSuccess(`Le bon de commande ${order.order_number} est maintenant marqué comme expédié.`);

      await loadOrder();
    } catch (error: any) {
      showError(error.response?.data?.detail || "Impossible de marquer la commande comme expédiée");
    }
  };

  const handleMarkDelivered = async () => {
    if (!order) return;

    try {
      await api.patch(`/purchase-orders/${id}`, {
        status: 'delivered'
      });

      showSuccess(`Le bon de commande ${order.order_number} est maintenant marqué comme livré.`);

      await loadOrder();
    } catch (error: any) {
      showError(error.response?.data?.detail || "Impossible de marquer la commande comme livrée");
    }
  };

  const handleRejectOrder = async () => {
    if (!order) return;

    try {
      await api.patch(`/purchase-orders/${id}`, {
        status: 'cancelled'
      });

      showSuccess(`Le bon de commande ${order.order_number} a été refusé et annulé.`);

      await loadOrder();
    } catch (error: any) {
      showError(error.response?.data?.detail || "Impossible de refuser la commande");
    }
  };


  const handleMarkEnregistrer = () => {
    setShowEnregistrerPopup(true);

    const today = new Date().toISOString().split('T')[0];
    setEnregistrerDate(today);
  };

  const handleConfirmEnregistrer = async () => {
    if (!order || !enregistrerDate) return;

    try {
      await api.patch(`/purchase-orders/${id}`, {
        status: 'paid',
        paid_at: enregistrerDate
      });

      showSuccess(`Le paiement pour la commande ${order.order_number} a été enregistré le ${enregistrerDate}.`);
      setShowEnregistrerPopup(false);
      setEnregistrerDate('');
      await loadOrder();
    } catch (error: any) {
      showError(error.response?.data?.detail || "Impossible d'enregistrer le paiement");
    }
  };


  const normalizedTierMap = useMemo(() => {
    if (!tierBreakdownParsed) return [] as UnitTypeTierData[];
    return Object.values(tierBreakdownParsed);
  }, [tierBreakdownParsed]);

  const getActiveTierForItem = (
    item: NonNullable<PurchaseOrder['items']>[number]
  ): TierInfo | null => {
    if (!normalizedTierMap.length) return null;
    const unitLabel = normalizeUnitName(getUnitLabel(item.product_unit));
    const batchSize = item.batch_size || 1;
    const match = normalizedTierMap.find((data) => {
      const tierUnit = normalizeUnitName(data.unit_name);
      return tierUnit === unitLabel && data.batch_size === batchSize;
    });
    if (!match) return null;
    return match.tiers.find((tier) => tier.triggered) ?? null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!order) return null;

  const currentStatus = statusOptions.find(s => s.value === order.status);
  const statusDateInfo = getStatusDateInfo(order);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/20 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="max-w-7xl mx-auto p-3 sm:p-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Button
              onClick={() => navigate('/achat/orders')}
              variant="ghost"
              className="gap-2 self-start"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour
            </Button>
            <div>
              <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 sm:gap-3">
                <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-orange-600" />
                <span className="break-all">{order.order_number}</span>
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
                Créé le {new Date(order.created_at).toLocaleDateString('fr-FR')}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {!editMode ? (
              <>
                <Button
                  onClick={() => setEditMode(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2 flex-1 sm:flex-none"
                >
                  <Edit2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Modifier</span>
                  <span className="sm:hidden">Éditer</span>
                </Button>
                {['pending', 'draft', 'cancelled'].includes(order.status) && (
                  <Button
                    onClick={handleDelete}
                    variant="destructive"
                    className="gap-2 flex-1 sm:flex-none"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Supprimer</span>
                    <span className="sm:hidden">Suppr.</span>
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  onClick={() => {
                    setEditMode(false);
                    setEditedOrder(order);
                  }}
                  variant="outline"
                  className="gap-2 flex-1 sm:flex-none"
                >
                  <X className="h-4 w-4" />
                  Annuler
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700 text-white gap-2 flex-1 sm:flex-none"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Actions rapides */}
        <div className="mb-4 sm:mb-6 bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-3 sm:p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap gap-2 sm:gap-3">
            {/* Bon de Commande (BDC) */}
            <Button
              onClick={handleViewBDC}
              variant="outline"
              className="gap-2 w-full sm:w-auto"
            >
              <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Bon de commande (BDC)</span>
              <span className="sm:hidden text-xs">BDC</span>
            </Button>

            {/* Bon de livraison (BDL) */}
            {['shipped', 'delivered', 'invoiced', 'enregistrer', 'paid'].includes(order.status) && (
              <Button
                onClick={handleViewDeliveryNote}
                variant="outline"
                className="gap-2 w-full sm:w-auto"
              >
                <Truck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Bon de livraison (BDL)</span>
                <span className="sm:hidden text-xs">BDL</span>
              </Button>
            )}

            {/* Facture */}
            {['delivered', 'invoiced', 'enregistrer', 'paid'].includes(order.status) && (!isApporteur || existingInvoice) && (
              <Button
                onClick={handleViewInvoice}
                variant="outline"
                className="gap-2 w-full sm:w-auto"
              >
                <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Facture</span>
                <span className="sm:hidden text-xs">Facture</span>
              </Button>
            )}

            {/* Boutons pour commande NOUVELLE (pending) */}
            {order.status === 'pending' && !isApporteur && (
              <>
                <Button
                  onClick={handleConfirmOrder}
                  variant="outline"
                  className="gap-2 w-full sm:w-auto bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/40 border-green-200 dark:border-green-700 text-green-700 dark:text-green-300"
                >
                  <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Valider la commande</span>
                  <span className="sm:hidden text-xs">Valider</span>
                </Button>

                <Button
                  onClick={handleRejectOrder}
                  variant="outline"
                  className="gap-2 w-full sm:w-auto bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 border-red-200 dark:border-red-700 text-red-700 dark:text-red-300"
                >
                  <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Refuser la commande</span>
                  <span className="sm:hidden text-xs">Refuser</span>
                </Button>
              </>
            )}

            {/* Bouton Marquer comme expédiée (confirmed -> shipped) */}
            {order.status === 'confirmed' && (
              <Button
                onClick={handleMarkShipped}
                variant="outline"
                className="gap-2 w-full sm:w-auto bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300"
              >
                <Truck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Marquer comme expédiée</span>
                <span className="sm:hidden text-xs">Expédier</span>
              </Button>
            )}

            {/* Bouton Marquer comme livrée (shipped -> delivered) */}
            {order.status === 'shipped' && (
              <Button
                onClick={handleMarkDelivered}
                variant="outline"
                className="gap-2 w-full sm:w-auto bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300"
              >
                <Truck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Marquer comme livrée</span>
                <span className="sm:hidden text-xs">Livrer</span>
              </Button>
            )}

            {/* Bouton Marquer comme payée (invoiced -> paid) */}
            {order.status === 'invoiced' && !isApporteur && (
              <Button
                onClick={handleMarkEnregistrer}
                variant="outline"
                className="gap-2 w-full sm:w-auto bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/40 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300"
              >
                <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Marquer comme payée</span>
                <span className="sm:hidden text-xs">Payé</span>
              </Button>
            )}

            {/* Boutons de facture - Actions supplémentaires supprimés car intégrés dans les boutons principaux */}
          </div>
        </div>

        {pdfType && (
          <div className="mb-6 bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                {pdfType === 'bdc' && (
                  <>
                    <FileText className="h-4 w-4" />
                    Bon de Commande
                  </>
                )}
                {pdfType === 'bdl' && (
                  <>
                    <Truck className="h-4 w-4" />
                    Bon de Livraison
                  </>
                )}
                {pdfType === 'invoice' && (
                  <>
                    <FileText className="h-4 w-4" />
                    Facture
                  </>
                )}
              </h4>
              <div className="flex items-center gap-2">
                <a
                  href={pdfUrl || '#'}
                  download
                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  title="Télécharger"
                  aria-label="Télécharger le PDF"
                >
                  <Download className="h-4 w-4" />
                </a>
                <button
                  onClick={closePdfViewer}
                  aria-label="Fermer le PDF"
                  title="Fermer"
                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {loadingPdf ? (
              <div className="flex items-center justify-center h-96 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-3"></div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Chargement du PDF...</p>
                </div>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                className="w-full h-[600px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white shadow-inner"
                title={`PDF ${pdfType}`}
              />
            ) : (
              <div className="flex items-center justify-center h-96 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-600 dark:text-slate-400">Impossible de charger le PDF</p>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-4 sm:space-y-6">
            {/* Status & Horizontal Timeline - Merged Card */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
              {/* Header with Status */}
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4 sm:mb-6">
                <div className="flex items-center gap-4">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Statut</h2>
                  {editMode ? (
                    <Select
                      value={editedOrder.status}
                      onValueChange={(value) => setEditedOrder({ ...editedOrder, status: value as any })}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map(status => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${currentStatus?.color}`}>
                      {currentStatus?.label}
                    </div>
                  )}
                </div>

                {statusDateInfo && !editMode && (
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 whitespace-nowrap">
                      {statusDateInfo.label}
                    </div>
                    <div className="text-sm text-slate-700 dark:text-slate-200 whitespace-nowrap">
                      {statusDateInfo.value}
                    </div>
                  </div>
                )}
              </div>

              {/* Reception confirmation badge */}
              {order.status === 'delivered' && order.received_at && !editMode && (
                <div className="mb-6 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      Réception confirmée le {new Date(order.received_at).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              )}

              {/* Horizontal Timeline */}
              <div className="relative">
                <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-4 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  Évolution de la commande
                </h3>

                {/* Timeline Container - Horizontal */}
                <div className="flex items-start justify-between gap-1 sm:gap-2 overflow-x-auto pb-2">
                  {getWorkflowSteps(order).map((step, index, array) => {
                    const StepIcon = step.icon;
                    const isLast = index === array.length - 1;

                    return (
                      <div key={index} className="flex-1 min-w-[52px] sm:min-w-[100px] flex flex-col items-center relative">
                        {/* Horizontal line connector */}
                        {!isLast && (
                          <div className={`absolute top-5 left-1/2 w-full h-0.5 ${step.status === 'completed' ? 'bg-green-500' :
                            step.status === 'current' ? 'bg-orange-500' :
                              step.status === 'cancelled' ? 'bg-red-300' :
                                'bg-slate-300 dark:bg-slate-600'
                            }`} />
                        )}

                        {/* Icon */}
                        <div className={`relative z-10 flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 ${step.status === 'completed' ? 'bg-green-100 border-green-500 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          step.status === 'current' ? 'bg-orange-100 border-orange-500 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                            step.status === 'cancelled' ? 'bg-red-100 border-red-300 text-red-500 dark:bg-red-900/30 dark:text-red-400' :
                              'bg-slate-100 border-slate-300 text-slate-400 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-500'
                          }`}>
                          <StepIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                        </div>

                        {/* Label & Date */}
                        <div className="mt-1 sm:mt-2 text-center">
                          <h4 className={`text-[10px] sm:text-xs font-semibold ${step.status === 'completed' ? 'text-slate-900 dark:text-slate-100' :
                            step.status === 'current' ? 'text-orange-700 dark:text-orange-400' :
                              step.status === 'cancelled' ? 'text-red-500 dark:text-red-400' :
                                'text-slate-400 dark:text-slate-500'
                            }`}>
                            {step.label}
                          </h4>
                          {step.date && (
                            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                              {new Date(step.date).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'short'
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Articles */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4 flex items-center gap-2">
                <Package className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                Articles ({order.items?.length || 0})
              </h2>
              <div className="space-y-3">
                {order.items?.map((item) => {
                  const batchSize: number = item.batch_size || 1;
                  const unitLabel: string = getUnitLabel(item.product_unit || item.consumable?.unit || 'unité');
                  const activeTier: TierInfo | null = getActiveTierForItem(item);
                  const hasDiscount: boolean = item.discount_percentage > 0;
                  const priceBeforeDiscount: number = hasDiscount
                    ? item.unit_price / (1 - item.discount_percentage / 100)
                    : item.unit_price;
                  const lineTotalBeforeDiscount: number = priceBeforeDiscount * item.quantity;
                  const savings: number = Math.max(0, lineTotalBeforeDiscount - item.line_total);
                  const pricePerUnit: number = item.unit_price / batchSize;
                  const pricePerUnitBefore: number = hasDiscount
                    ? priceBeforeDiscount / batchSize
                    : pricePerUnit;
                  const tierLabel: string | null = activeTier
                    ? activeTier.discount_percentage
                      ? `Palier ${activeTier.tier_number} (-${activeTier.discount_percentage}%)`
                      : `Palier ${activeTier.tier_number} activé`
                    : null;

                  return (
                    <div
                      key={item.id}
                      className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-2 sm:gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 break-words">
                            {item.product_title || item.consumable?.title || `Article #${item.consumable_id}`}
                          </p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              Quantité: {item.quantity} × {unitLabel}
                            </span>
                            {batchSize > 1 && (
                              <span className="text-xs px-2 py-0.5 bg-slate-200 dark:bg-slate-700 rounded-full">
                                Lot de {batchSize} unité
                              </span>
                            )}
                          </div>
                        </div>
                        {tierLabel && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-semibold">
                            <Gift className="h-3 w-3" />
                            {tierLabel}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-4 pt-2 sm:pt-3 mt-2 sm:mt-3 border-t border-slate-200 dark:border-slate-700">
                        <div className="min-w-0">
                          <p className="text-[10px] sm:text-xs text-slate-500 mb-0.5 sm:mb-1">Prix unit. HT</p>
                          <div className="flex items-baseline gap-1 sm:gap-2 flex-wrap">
                            {hasDiscount && (
                              <span className="text-[10px] sm:text-sm text-slate-400 line-through">
                                {pricePerUnitBefore.toFixed(2)}€
                              </span>
                            )}
                            <span className="text-sm sm:text-lg font-bold text-slate-900 dark:text-slate-100">
                              {pricePerUnit.toFixed(2)}€
                            </span>
                          </div>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] sm:text-xs text-slate-500 mb-0.5 sm:mb-1">Prix lot HT</p>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-baseline gap-2 min-w-0">
                              {hasDiscount && (
                                <span className="text-[10px] sm:text-sm text-slate-400 line-through whitespace-nowrap">
                                  {priceBeforeDiscount.toFixed(2)}€
                                </span>
                              )}
                              <span className="text-sm sm:text-lg font-bold text-slate-900 dark:text-slate-100 whitespace-nowrap">
                                {item.unit_price.toFixed(2)}€
                              </span>
                            </div>
                            {hasDiscount && (
                              <span className="shrink-0 text-xs px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 font-medium">
                                −{item.discount_percentage}%
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-span-2 sm:col-span-1 lg:text-right">
                          <p className="text-[10px] sm:text-xs text-slate-500 mb-0.5 sm:mb-1">Total ligne HT</p>
                          <div className="flex flex-col lg:items-end gap-1">
                            <span className="text-base sm:text-xl font-bold text-orange-600">
                              {item.line_total.toFixed(2)} € HT
                            </span>
                            {hasDiscount && (
                              <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-md shadow-sm">
                                <Gift className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">
                                  Gain: −{savings.toFixed(2)} € HT
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {['pending', 'draft'].includes(order.status) && (
                        <div className="flex justify-end mt-3">
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-all"
                            title="Supprimer cette ligne"
                            type="button"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="flex justify-between text-lg font-bold text-slate-900 dark:text-slate-100">
                  <span>Total TTC</span>
                  <span className="text-orange-600">{order.total_amount.toFixed(2)} {order.currency}</span>
                </div>
              </div>
            </div>

            {/* Delivery Address */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4 flex items-center gap-2">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                Adresse de livraison
              </h2>
              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <Label>Adresse</Label>
                    <Input
                      value={editedOrder.delivery_address || ''}
                      onChange={(e) => setEditedOrder({ ...editedOrder, delivery_address: e.target.value })}
                      placeholder="Rue et numéro"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Code postal</Label>
                      <Input
                        value={editedOrder.delivery_postal_code || ''}
                        onChange={(e) => setEditedOrder({ ...editedOrder, delivery_postal_code: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Ville</Label>
                      <Input
                        value={editedOrder.delivery_city || ''}
                        onChange={(e) => setEditedOrder({ ...editedOrder, delivery_city: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Pays</Label>
                    <Input
                      value={editedOrder.delivery_country || ''}
                      onChange={(e) => setEditedOrder({ ...editedOrder, delivery_country: e.target.value })}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-slate-700 dark:text-slate-300 space-y-1">
                  {order.delivery_address && <p>{order.delivery_address}</p>}
                  <p>
                    {order.delivery_postal_code && <span>{order.delivery_postal_code} </span>}
                    {order.delivery_city && <span>{order.delivery_city}</span>}
                  </p>
                  {order.delivery_country && <p>{order.delivery_country}</p>}
                  {!order.delivery_address && !order.delivery_city && (
                    <p className="text-slate-400 italic">Aucune adresse de livraison</p>
                  )}
                </div>
              )}
            </div>

            {/* Order Tracking */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4 flex items-center gap-2">
                <Truck className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                Suivi de commande
              </h2>
              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <Label>Numéro de suivi</Label>
                    <Input
                      value={editedOrder.tracking_number || ''}
                      onChange={(e) => setEditedOrder({ ...editedOrder, tracking_number: e.target.value })}
                      placeholder="Entrez le numéro de suivi"
                    />
                  </div>
                  <div>
                    <Label>Localisation actuelle</Label>
                    <Input
                      value={editedOrder.current_location || ''}
                      onChange={(e) => setEditedOrder({ ...editedOrder, current_location: e.target.value })}
                      placeholder="Ex: Entrepôt Paris, En transit, Livreur local..."
                    />
                  </div>
                  <div>
                    <Label>Date de livraison estimée</Label>
                    <Input
                      type="date"
                      value={editedOrder.estimated_delivery_date ? new Date(editedOrder.estimated_delivery_date).toISOString().split('T')[0] : ''}
                      onChange={(e) => setEditedOrder({ ...editedOrder, estimated_delivery_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Notes de suivi (visibles par le client)</Label>
                    <Textarea
                      value={editedOrder.tracking_notes || ''}
                      onChange={(e) => setEditedOrder({ ...editedOrder, tracking_notes: e.target.value })}
                      placeholder="Informations sur le suivi de la commande..."
                      rows={3}
                    />
                  </div>
                  {editedOrder.last_tracking_update && (
                    <p className="text-sm text-slate-500">
                      Dernière mise à jour: {new Date(editedOrder.last_tracking_update).toLocaleString('fr-FR')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-slate-700 dark:text-slate-300 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-slate-500">Numéro de suivi:</span>
                      <p className="font-mono">{order.tracking_number || <span className="text-slate-400 italic">Non défini</span>}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-slate-500">Localisation actuelle:</span>
                      <p>{order.current_location || <span className="text-slate-400 italic">Non définie</span>}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-slate-500">Date de livraison estimée:</span>
                      <p>{order.estimated_delivery_date ? new Date(order.estimated_delivery_date).toLocaleDateString('fr-FR') : <span className="text-slate-400 italic">Non définie</span>}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-slate-500">Dernière mise à jour:</span>
                      <p>{order.last_tracking_update ? new Date(order.last_tracking_update).toLocaleString('fr-FR') : <span className="text-slate-400 italic">Jamais</span>}</p>
                    </div>
                  </div>
                  {order.tracking_notes && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                      <span className="text-sm font-medium text-slate-500">Notes de suivi:</span>
                      <p className="mt-1">{order.tracking_notes}</p>
                    </div>
                  )}
                  {!order.tracking_number && !order.current_location && !order.estimated_delivery_date && (
                    <p className="text-slate-400 italic">Aucune information de suivi disponible</p>
                  )}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                Notes
              </h2>
              {editMode ? (
                <Textarea
                  value={editedOrder.notes || ''}
                  onChange={(e) => setEditedOrder({ ...editedOrder, notes: e.target.value })}
                  placeholder="Notes sur la commande..."
                  rows={4}
                />
              ) : (
                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {order.notes || <span className="text-slate-400 italic">Aucune note</span>}
                </p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Client Info */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4 flex items-center gap-2">
                <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                Client
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Entreprise</p>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {order.client?.company_name || `Client #${order.client_id}`}
                  </p>
                </div>
                {(order.client?.first_name || order.client?.last_name) && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-700 dark:text-slate-300">{`${order.client?.first_name ?? ''} ${order.client?.last_name ?? ''}`.trim()}</span>
                  </div>
                )}
                {order.client?.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <a href={`mailto:${order.client.email}`} className="text-blue-600 hover:underline">
                      {order.client.email}
                    </a>
                  </div>
                )}
                {order.client?.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <a href={`tel:${order.client.phone}`} className="text-blue-600 hover:underline">
                      {order.client.phone}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Apporteur d'affaire Info */}
            {order.client?.admin && (
              <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4 flex items-center gap-2">
                  <User className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  Apporteur d'affaire
                </h2>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Nom</p>
                    <p className="font-medium text-slate-900 dark:text-slate-100">
                      {order.client.admin.first_name && order.client.admin.last_name
                        ? `${order.client.admin.first_name} ${order.client.admin.last_name}`
                        : order.client.admin.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <a href={`mailto:${order.client.admin.email}`} className="text-blue-600 hover:underline">
                      {order.client.admin.email}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 rounded-xl border-2 border-orange-200 dark:border-orange-800 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4">Résumé</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">Sous-total HT</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {order.subtotal.toFixed(2)} {order.currency}
                  </span>
                </div>
                {((order as any).credit_note_amount > 0 || (order as any).credit_note_percentage > 0) && (
                  <>
                    <div className="flex justify-between text-sm text-amber-600">
                      <span>Remise supplémentaire {(order as any).credit_note_type === 'percentage' && (order as any).credit_note_percentage > 0 ? `(${(order as any).credit_note_percentage}%)` : ''} {(order as any).credit_note_reason ? `– ${(order as any).credit_note_reason}` : ''}</span>
                      <span className="font-semibold">-{((order as any).credit_note_amount || 0).toFixed(2)} {order.currency}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold">
                      <span className="text-slate-700 dark:text-slate-300">Net HT</span>
                      <span className="text-slate-900 dark:text-slate-100">
                        {(order.subtotal - ((order as any).credit_note_amount || 0)).toFixed(2)} {order.currency}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">TVA</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {order.tax_amount.toFixed(2)} {order.currency}
                  </span>
                </div>
                <div className="pt-3 border-t border-orange-200 dark:border-orange-800 flex justify-between">
                  <span className="font-bold text-slate-900 dark:text-slate-100">Total TTC</span>
                  <span className="font-bold text-xl text-orange-600">
                    {order.total_amount.toFixed(2)} {order.currency}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Date de Paiement */}
      {showEnregistrerPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 fade-in duration-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  Enregistrer le paiement
                </h3>
                <button
                  type="button"
                  onClick={() => setShowEnregistrerPopup(false)}
                  aria-label="Fermer"
                  title="Fermer"
                  className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
                >
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                Veuillez indiquer la date à laquelle le paiement pour la commande {order?.order_number} a été reçu.
              </p>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="payment-date" className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                    Date de paiement
                  </Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="payment-date"
                      type="date"
                      value={enregistrerDate}
                      onChange={(e) => setEnregistrerDate(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <Button
                  onClick={() => setShowEnregistrerPopup(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleConfirmEnregistrer}
                  className="flex-1 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-lg shadow-orange-500/20"
                >
                  Confirmer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, type: deleteDialog.type, itemId: deleteDialog.itemId })}
        title={deleteDialog.type === 'order' ? 'Supprimer le bon de commande' : 'Supprimer la ligne'}
        description={
          deleteDialog.type === 'order'
            ? 'Êtes-vous sûr de vouloir supprimer ce bon de commande ? Cette action est irréversible.'
            : 'Êtes-vous sûr de vouloir supprimer cette ligne ? Cette action est irréversible.'
        }
        confirmText="Supprimer"
        cancelText="Annuler"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default PurchaseOrderDetail;
